import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Printer, EyeOff, RefreshCw, Package, ChevronDown, ChevronUp, Layers, FileText, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

/**
 * The Mujahez's "قائمة الوصول من الأدمن" panel — receives invoices the
 * admin dispatched via the "📤 للمُجهّز" button. Two sections:
 *   • مفردة   → invoice-per-invoice, mujahez picks each individually
 *   • مُجمّعة  → one pre-aggregated pick-list (image + name + total qty)
 * No debts, no prices — printing only. Feb 17, 2026 request.
 */
const MujahezPickQueue = () => {
  const [data, setData] = useState({ individual: [], batches: [], counts: { individual: 0, batches: 0 } });
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [openBatch, setOpenBatch] = useState(null);
  const [search, setSearch] = useState("");
  const [hidingBusy, setHidingBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/mujahez/queue`);
      setData(res || { individual: [], batches: [], counts: {} });
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    // Auto-refresh every 30s so new dispatches show up quickly
    const iv = setInterval(load, 30_000);
    return () => clearInterval(iv);
  }, [load]);

  const markPrinted = async (id) => {
    try {
      await axios.post(`${API}/mujahez/orders/${id}/printed`);
      // Optimistic local update
      setData((d) => ({
        ...d,
        individual: d.individual.map((o) => o.id === id ? { ...o, mujahez_printed_at: new Date().toISOString() } : o),
        batches: d.batches.map((b) => ({ ...b, orders: b.orders.map((o) => o.id === id ? { ...o, mujahez_printed_at: new Date().toISOString() } : o) })),
      }));
    } catch { /* silent */ }
  };

  const printIndividual = (o) => {
    const w = window.open("", "_blank");
    w.document.write(buildInvoiceHtml(o));
    w.document.close();
    markPrinted(o.id);
  };

  const printBatch = (b) => {
    const w = window.open("", "_blank");
    w.document.write(buildBatchHtml(b));
    w.document.close();
    // Mark all orders in batch as printed
    b.orders.forEach((o) => markPrinted(o.id));
  };

  const hideAllPrinted = async () => {
    if (!window.confirm("إخفاء كل الفواتير المطبوعة من قائمتك؟ (تبقى محفوظة بالنظام)")) return;
    setHidingBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/mujahez/hide-printed`);
      toast.success(`✓ تم إخفاء ${res.hidden} فاتورة مطبوعة`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإخفاء");
    } finally { setHidingBusy(false); }
  };

  const q = search.trim().toLowerCase();
  const filteredIndiv = q
    ? data.individual.filter((o) =>
        String(o.invoice_number).includes(q) ||
        (o.user_name || "").toLowerCase().includes(q) ||
        (o.market_name || "").toLowerCase().includes(q))
    : data.individual;

  return (
    <div className="space-y-3" data-testid="mujahez-pick-queue">
      {/* Toolbar */}
      <div className="bg-gradient-to-l from-[#7C3AED] to-[#0EA5E9] text-white rounded-2xl p-3 flex items-center gap-2 shadow-lg">
        <Layers className="w-5 h-5" />
        <div className="flex-1">
          <p className="font-black text-sm">قائمة الوصول من الإدارة</p>
          <p className="text-[10px] opacity-90">
            {data.counts.individual || 0} مفردة · {data.counts.batches || 0} مُجمّعة
          </p>
        </div>
        <button onClick={load} className="bg-white/20 hover:bg-white/30 rounded-full p-2" title="تحديث">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
        <button
          onClick={hideAllPrinted}
          disabled={hidingBusy}
          data-testid="mujahez-hide-printed"
          className="bg-white/20 hover:bg-white/30 rounded-full px-3 py-2 text-[11px] font-bold flex items-center gap-1"
        >
          <EyeOff className="w-3.5 h-3.5" /> إخفاء المطبوعة
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="🔍 ابحث برقم الفاتورة أو الاسم..."
        data-testid="mujahez-search"
        className="w-full px-3 py-2 rounded-xl border-2 border-[#E4EBE6] focus:border-[#7C3AED] text-sm"
      />

      {/* Aggregated batches */}
      {data.batches.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-black text-[#0EA5E9] flex items-center gap-1">
            <Package className="w-4 h-4" /> قوائم مُجمّعة ({data.batches.length})
          </p>
          {data.batches.map((b) => {
            const isOpen = openBatch === b.batch_id;
            const totalItems = b.aggregated_items.length;
            const totalQty = b.aggregated_items.reduce((s, i) => s + i.quantity, 0);
            const allPrinted = b.orders.every((o) => o.mujahez_printed_at);
            return (
              <div key={b.batch_id} className={`rounded-2xl border-2 ${allPrinted ? "bg-[#DCFCE7] border-[#15803D]/40" : "bg-[#EFF6FF] border-[#0EA5E9]/40"}`}>
                <button onClick={() => setOpenBatch(isOpen ? null : b.batch_id)}
                  className="w-full flex items-center gap-2 p-3 text-right">
                  <div className="bg-[#0EA5E9] text-white rounded-lg p-2 shrink-0">
                    <Package className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-sm text-[#1E3A5F]">
                      {b.orders.length} فاتورة · {totalItems} صنف · {fmt(totalQty)} قطعة
                    </p>
                    <p className="text-[10px] text-[#5C6E64]" dir="ltr">
                      {new Date(b.sent_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                    </p>
                  </div>
                  {allPrinted && <CheckCircle2 className="w-4 h-4 text-[#15803D]" />}
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {isOpen && (
                  <div className="border-t border-[#0EA5E9]/30 p-3 space-y-1.5">
                    {b.aggregated_items.map((it, i) => (
                      <div key={i} className="flex items-center gap-2 bg-white rounded-lg p-2">
                        <div className="w-10 h-10 bg-[#F8FAFC] rounded shrink-0 flex items-center justify-center">
                          {it.image_path
                            ? <img src={`${API}/files/${it.image_path}`} alt="" className="w-full h-full object-contain" onError={(e) => e.currentTarget.style.display = "none"} />
                            : <Package className="w-4 h-4 text-[#5C6E64]" />}
                        </div>
                        <p className="text-xs font-bold text-[#1E3A5F] flex-1 truncate">{it.name_ar}</p>
                        <div className="bg-[#FEF08A] text-[#78350F] rounded px-2 py-0.5 font-black text-sm" dir="ltr">{fmt(it.quantity)}</div>
                      </div>
                    ))}
                    <button
                      onClick={() => printBatch(b)}
                      data-testid={`mujahez-print-batch-${b.batch_id}`}
                      className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-black rounded-full py-2 mt-2 flex items-center justify-center gap-2"
                    >
                      <Printer className="w-4 h-4" /> اطبع القائمة المُجمّعة
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Individual */}
      {filteredIndiv.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-black text-[#7C3AED] flex items-center gap-1">
            <FileText className="w-4 h-4" /> فواتير مفردة ({filteredIndiv.length})
          </p>
          {filteredIndiv.map((o) => {
            const isOpen = openId === o.id;
            const printed = Boolean(o.mujahez_printed_at);
            const totalQty = (o.items || []).reduce((s, it) => s + Number(it.quantity || 0), 0);
            return (
              <div key={o.id} className={`rounded-2xl border-2 ${printed ? "bg-[#DCFCE7] border-[#15803D]/40" : "bg-white border-[#7C3AED]/30"}`}>
                <button onClick={() => setOpenId(isOpen ? null : o.id)} className="w-full flex items-center gap-2 p-3 text-right">
                  <div className={`rounded-lg p-2 shrink-0 text-white ${printed ? "bg-[#15803D]" : "bg-[#7C3AED]"}`}>
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-sm text-[#1E3A5F]" dir="ltr">#{o.invoice_number}</p>
                    <p className="text-xs text-[#5C6E64] truncate">{o.market_name || o.user_name}</p>
                    <p className="text-[10px] text-[#0EA5E9]">{(o.items || []).length} صنف · {fmt(totalQty)} قطعة</p>
                  </div>
                  {printed && <CheckCircle2 className="w-4 h-4 text-[#15803D]" />}
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {isOpen && (
                  <div className="border-t border-[#7C3AED]/30 p-3 space-y-1">
                    {(o.items || []).map((it, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="w-5 text-center text-[#5C6E64]">{i + 1}</span>
                        <span className="flex-1 truncate font-bold">{it.name_ar}</span>
                        <span className="bg-[#FEF08A] text-[#78350F] rounded px-2 font-black" dir="ltr">{fmt(it.quantity)}</span>
                      </div>
                    ))}
                    <button
                      onClick={() => printIndividual(o)}
                      data-testid={`mujahez-print-order-${o.id}`}
                      className="w-full bg-[#7C3AED] hover:bg-[#6d28d9] text-white font-black rounded-full py-2 mt-2 flex items-center justify-center gap-2"
                    >
                      <Printer className="w-4 h-4" /> اطبع الفاتورة
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && data.individual.length === 0 && data.batches.length === 0 && (
        <div className="text-center py-8 text-[#5C6E64]">
          <Package className="w-12 h-12 mx-auto opacity-30 mb-2" />
          <p className="text-sm">لا توجد فواتير جديدة من الإدارة</p>
        </div>
      )}
    </div>
  );
};

// Printable HTML for a single order — mujahez view (no logo, no prices)
function buildInvoiceHtml(o) {
  const rows = (o.items || []).map((it, i) => `
    <tr>
      <td class="c">${i + 1}</td>
      <td class="r">${(it.name_ar || it.name || "").replace(/</g, "&lt;")}</td>
      <td class="c qty">${Number(it.quantity || 0).toLocaleString("en-US")}</td>
    </tr>`).join("");
  return commonHtml(
    `فاتورة #${o.invoice_number || ""} — ${(o.market_name || o.user_name || "").replace(/</g, "&lt;")}`,
    (o.items || []).length,
    rows,
    false,
  );
}

// Printable HTML for a batch pick-list — aggregated
function buildBatchHtml(b) {
  const rows = (b.aggregated_items || []).map((it, i) => `
    <tr>
      <td class="c">${i + 1}</td>
      <td class="c img">${it.image_path
        ? `<img src="${process.env.REACT_APP_BACKEND_URL}/api/files/${it.image_path}" class="pi" onerror="this.style.display='none'"/>`
        : "—"}</td>
      <td class="r">${(it.name_ar || "").replace(/</g, "&lt;")}</td>
      <td class="c qty">${Number(it.quantity || 0).toLocaleString("en-US")}</td>
    </tr>`).join("");
  return commonHtml(
    `قائمة مُجمّعة — ${b.orders.length} فاتورة`,
    b.aggregated_items.length,
    rows,
    true,
  );
}

function commonHtml(title, itemCount, rows, withImage) {
  const now = new Date().toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  return `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"/>
<title>${title}</title>
<style>
  @page { size: A4; margin: 8mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; }
  body { font-family: system-ui,sans-serif; color: #000; margin: 0; }
  .hd { background: #7C3AED; color: #fff; padding: 8px 12px; border-radius: 8px; margin-bottom: 8px; display: flex; justify-content: space-between; }
  .hd h1 { margin: 0; font-size: 16px; font-weight: 900; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th, td { border: 1.5px solid #000; padding: 5px; }
  th { background: #1E3A5F; color: #fff; font-weight: 900; }
  td.c { text-align: center; }
  td.r { text-align: right; font-weight: 800; }
  td.qty { font-weight: 900; font-size: 18px; background: #FEF08A; color: #78350F; }
  td.img { padding: 3px; width: 55px; }
  .pi { width: 42px; height: 42px; object-fit: contain; display: block; margin: 0 auto; }
</style></head><body>
<div class="hd">
  <div><h1>${title}</h1><div style="font-size:11px;opacity:.9">${itemCount} صنف</div></div>
  <div style="font-size:11px">${now}</div>
</div>
<table>
  <thead>
    <tr>
      <th style="width:30px">ت</th>
      ${withImage ? '<th style="width:55px">صورة</th>' : ''}
      <th>المادة</th>
      <th style="width:80px">العدد</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
</table>
<script>window.onload=()=>setTimeout(()=>window.print(),500);<\/script>
</body></html>`;
}

export default MujahezPickQueue;
